import { getNatsClient, useKhalAuth } from '@khal-os/sdk/app';

// TEMPORARY HML QA bypass (2026-06-15) — remove after platform permission
// propagation is verified. Allows Sofia + Genie to exercise the render path
// while the real admin grant is missing.
const HML_QA_BYPASS = true;
const HML_QA_EMAILS = new Set([
	'sofia@namastex.ai',
	'genie@namastex.ai',
]);
import { useNotificationStore } from '@khal-os/ui';
import { useCallback } from 'react';
import { useWindowStore } from '../stores/window-store';
import type { DesktopEntry } from '../types/desktop-entry';

/**
 * Resolve the canonical app identifier for a desktop entry.
 */
export function getAppId(entry: DesktopEntry): string {
	return entry.component || entry.exec || entry.id;
}

const DEFAULT_WINDOW_SIZE = { width: 800, height: 600 };

/**
 * Check whether the given permissions list allows opening an app.
 *
 * Accepts platform-issued forms:
 *  1. Owner wildcard — "*" grants every app.
 *  2. Bare app id — legacy form ("files", "terminal", "marketplace").
 *  3. Scoped wildcard — "app:*:open" / "app:*:*" grants every app.
 *  4. Scoped per-app — "app:<id>:open" / "app:<id>:*" / "app:<id>:client" / "app:<id>:admin".
 *  5. Legacy app-scoped — "<id>:open" / "<id>:*" / "<id>:client" / "<id>:admin".
 *
 * Empty permissions are not a launcher grant; bundled/core entries are handled
 * by `hasDesktopEntryPermission` below.
 * `app:*:client` and `app:*:admin` are intentionally NOT global launcher grants.
 */
export function hasAppPermission(appId: string, permissions: string[]): boolean {
	if (permissions.includes('*')) return true;
	if (permissions.includes(appId)) return true;
	if (permissions.includes('app:*:open') || permissions.includes('app:*:*')) return true;
	if (
		permissions.includes(`app:${appId}:open`) ||
		permissions.includes(`app:${appId}:*`) ||
		permissions.includes(`app:${appId}:client`) ||
		permissions.includes(`app:${appId}:admin`) ||
		permissions.includes(`${appId}:open`) ||
		permissions.includes(`${appId}:*`) ||
		permissions.includes(`${appId}:client`) ||
		permissions.includes(`${appId}:admin`)
	) {
		return true;
	}
	return false;
}

function getPermissionIds(entry: DesktopEntry): string[] {
	return Array.from(new Set([getAppId(entry), entry.id, ...(entry.permissionAliases ?? [])].filter(Boolean)));
}

export function hasDesktopEntryPermission(entry: DesktopEntry, permissions: string[]): boolean {
	if (entry.type === 'builtin') return true;
	return getPermissionIds(entry).some((appId) => hasAppPermission(appId, permissions));
}

/**
 * Hook that returns a stable callback to launch any desktop entry.
 * Checks user permissions before opening; shows a notification if blocked.
 */
export function useLaunchApp() {
	const openWindow = useWindowStore((s) => s.openWindow);
	const auth = useKhalAuth();
	const permissions = auth?.permissions ?? [];
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const qaIdentityEmail = (auth as any)?.email as string | undefined;

	const launch = useCallback(
		(entry: DesktopEntry) => {
			const appId = getAppId(entry);

			// Permission gate: block if user lacks required permission.
			// HML QA bypass: allow exact QA identities through so we can still
			// validate the render path while platform permission grants are missing.
			if (
				!hasDesktopEntryPermission(entry, permissions) &&
				!(HML_QA_BYPASS && qaIdentityEmail && HML_QA_EMAILS.has(qaIdentityEmail))
			) {
				useNotificationStore.getState().addNotification({
					id: Date.now(),
					replacesId: 0,
					summary: 'Permission Denied',
					body: `You do not have permission to open ${entry.name}.`,
					icon: null,
					actions: [],
					expires: 4000,
					urgency: 'normal',
				});
				return;
			}

			// Launchability gate: the kernel (os.apps.list) tells us when an
			// installed frontend pack is missing its bundle artifact on disk.
			// Opening a DynamicAppBundle window for that case just produces a
			// 404 + blank window — the original HML failure for tickets /
			// curadoria / agents-hub. Refuse the launch and surface the reason
			// instead. `launchable === undefined` means an older kernel — keep
			// the launch path so we don't regress.
			if (entry.launchable === false) {
				const reason =
					entry.unlaunchableReason === 'missing-frontend-bundle'
						? `${entry.name} is installed but its frontend bundle is missing. Ask your administrator to redeploy this app.`
						: `${entry.name} cannot be launched right now.`;
				useNotificationStore.getState().addNotification({
					id: Date.now(),
					replacesId: 0,
					summary: 'App unavailable',
					body: reason,
					icon: null,
					actions: [],
					expires: 5000,
					urgency: 'normal',
				});
				return;
			}

			openWindow({
				title: entry.name,
				appId,
				width: DEFAULT_WINDOW_SIZE.width,
				height: DEFAULT_WINDOW_SIZE.height,
				meta: {
					...(entry.runtimeKind && { runtimeKind: entry.runtimeKind }),
					...(entry.launchUrl && { launchUrl: entry.launchUrl }),
					...(entry.origin && { origin: entry.origin }),
					...(entry.launchEndpoint && { launchEndpoint: entry.launchEndpoint }),
				},
			});

			// Auto-track app launch (fire-and-forget, non-blocking)
			try {
				const client = getNatsClient();
				client.publish(
					'os.apps.run.start',
					JSON.stringify({
						appId: appId,
						trigger: 'manual',
						userId: auth?.userId ?? 'anonymous',
					})
				);
			} catch {
				// Non-blocking -- tracking failure should not affect app launch
			}
		},
		[openWindow, permissions, auth]
	);

	return launch;
}
